import type { CommitmentInternalTransferSenderVersion } from '@festina/life-general-types/life'
import {
  type Compare,
  listUtils,
  notEmpty,
  stringUtils,
} from '@festina-life/core'
import { useTranslate, useTranslateEnum } from '@festina-life/ui/hooks'
import {
  createColumnDefiner,
  TableWithCompare,
} from '@festina-life/ui/listsAndTables'
import { CardBody } from '@festina-life/ui/structure'
import { type FC, useMemo } from 'react'

import { useAmountFormatter } from '../../hooks/useAmountFormatter'
import { useCommitmentVersion } from '../../hooks/useCommitmentVersion'
import { useHook } from '../../lenteHooks'
import { withLifeErrorBoundary } from '../../libs/withLifeErrorBoundary'
import { LifeCard } from '../card/LifeCard'
import { aggregateDataPerClaimReceiver } from './commitmentDetailCardsUtility'
import { DeathClaimsNameColumn } from './DeathClaimsNameColumn'

const defineColumn =
  createColumnDefiner<CommitmentInternalTransferSenderVersion>()

const DeathClaimsCardInternal: FC = () => {
  const {
    currentCommitmentVersion: commitmentVersion,
    previousCommitmentVersion,
    isDiffing,
  } = useCommitmentVersion()

  const translate = useTranslate()
  const translateEnum = useTranslateEnum()
  const { format } = useAmountFormatter()

  const internalIdMap = useMemo(() => {
    const receiverIds = (
      commitmentVersion?.commitmentInternalTransferSenderVersions ?? []
    )
      .map((x) => x?.receiverId)
      .filter(notEmpty)
    const prevReceiverIds = (
      previousCommitmentVersion?.commitmentInternalTransferSenderVersions ?? []
    )
      .map((x) => x?.receiverId)
      .filter(notEmpty)

    return receiverIds.concat(prevReceiverIds)
  }, [
    commitmentVersion?.commitmentInternalTransferSenderVersions,
    previousCommitmentVersion?.commitmentInternalTransferSenderVersions,
  ])

  const entityNameMap = useHook('pages.crm.useResolveIdToEntityName')(
    internalIdMap,
  )

  const pairs: Compare<CommitmentInternalTransferSenderVersion>[] =
    listUtils.getListOfPairs({
      currentList: aggregateDataPerClaimReceiver(
        (
          commitmentVersion?.commitmentInternalTransferSenderVersions ?? []
        ).filter(notEmpty),
      ),

      prevList: aggregateDataPerClaimReceiver(
        (
          previousCommitmentVersion?.commitmentInternalTransferSenderVersions ??
          []
        ).filter(notEmpty),
      ),
      keyFunc: (x) =>
        `${x.commandId}${x.receiverId}${x.relationshipType}${x.toCoverTemplateId}${x.toPolicyTemplateId}`,
    })

  const columns = {
    policy: defineColumn({
      heading: translate('deathClaims.policy'),
      keyPath: 'toPolicyTemplateId',
    }),
    cover: defineColumn({
      heading: translate('deathClaims.covers'),
      keyPath: 'toCoverTemplateId',
    }),
    type: defineColumn({
      heading: translate('deathClaims.relationshipType'),
      keyPath: 'relationshipType',
      formatter: (s) => translateEnum('life.enum.RelationshipType', s),
    }),
    name: defineColumn({
      heading: translate('deathClaims.receiverName'),
      keyPath: 'receiverId',
      formatter: (receiverId) =>
        receiverId && (
          <DeathClaimsNameColumn
            secondaryId={receiverId}
            name={entityNameMap[receiverId].name}
            type={entityNameMap[receiverId].type}
          />
        ),
    }),
    benefit: defineColumn({
      heading: translate('deathClaims.benefit'),
      keyPath: 'benefit',
      formatter: format,
      alignment: 'right',
    }),
  }

  const deadStatuses = ['death', 'deathRegistered'] as const
  const isEitherCommitmentDead =
    stringUtils.strictIncludes(
      commitmentVersion?.commitmentStatus,
      deadStatuses,
    ) ||
    stringUtils.strictIncludes(
      previousCommitmentVersion?.commitmentStatus,
      deadStatuses,
    )

  return (
    <>
      {isEitherCommitmentDead && (
        <LifeCard
          className="col-span-2"
          headerProps={{ title: translate('deathClaimsCard.title') }}
        >
          <CardBody outerClassName="max-h-72">
            <TableWithCompare
              columns={columns}
              rowPairs={pairs}
              isDiffing={isDiffing}
            />
          </CardBody>
        </LifeCard>
      )}
    </>
  )
}

export const DeathClaimsCard = withLifeErrorBoundary(DeathClaimsCardInternal)
